import { useEffect, useMemo, useState } from "react";

import {
  findCrossStyleConflicts,
  inspectEmphasisTermsText,
  type EmphasisStyle,
} from "../domain/text-emphasis";
import type { EmphasisRules } from "../domain/templates";

const RULE_FIELDS: Array<{
  key: EmphasisStyle;
  label: string;
  description: string;
  mark: string;
  markClass: string;
}> = [
  {
    key: "bold",
    label: "自动加粗",
    description: "命中后使用粗体",
    mark: "B",
    markClass: "bold",
  },
  {
    key: "italic",
    label: "自动斜体",
    description: "命中后使用斜体",
    mark: "I",
    markClass: "italic",
  },
  {
    key: "boldItalic",
    label: "自动加粗并斜体",
    description: "命中后同时使用粗体与斜体",
    mark: "BI",
    markClass: "bold-italic",
  },
];

function rulesToDrafts(rules: EmphasisRules): Record<EmphasisStyle, string> {
  return {
    bold: rules.bold.join("\n"),
    italic: rules.italic.join("\n"),
    boldItalic: rules.boldItalic.join("\n"),
  };
}

function sameTerms(left: string[], right: string[]): boolean {
  return (
    left.length === right.length && left.every((term, index) => term === right[index])
  );
}

export function EmphasisRulesEditor({
  rules,
  onChange,
  compact = false,
}: {
  rules: EmphasisRules;
  onChange: (rules: EmphasisRules) => void;
  compact?: boolean;
}) {
  const [drafts, setDrafts] = useState(() => rulesToDrafts(rules));

  useEffect(() => {
    setDrafts((current) => {
      const next = { ...current };
      let changed = false;
      for (const field of RULE_FIELDS) {
        const inspection = inspectEmphasisTermsText(current[field.key]);
        if (inspection.valid && !sameTerms(inspection.terms, rules[field.key])) {
          next[field.key] = rules[field.key].join("\n");
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [rules.bold, rules.boldItalic, rules.italic]);

  const conflicts = useMemo(() => findCrossStyleConflicts(rules), [rules]);
  const total = rules.bold.length + rules.italic.length + rules.boldItalic.length;

  return (
    <div className={`emphasis-rules-editor${compact ? " compact" : ""}`}>
      <div className="emphasis-rules-grid">
        {RULE_FIELDS.map((field) => (
          <label className="emphasis-rule-field" key={field.key}>
            <span className="emphasis-rule-field-heading">
              <span className={`emphasis-rule-mark ${field.markClass}`} aria-hidden="true">
                {field.mark}
              </span>
              <span>
                <strong>{field.label}</strong>
                <small>{field.description}</small>
              </span>
              <b>{rules[field.key].length} 条</b>
            </span>
            <textarea
              aria-label={`${field.label}词或短语`}
              aria-invalid={!inspectEmphasisTermsText(drafts[field.key]).valid}
              maxLength={40_200}
              onChange={(event) => {
                const value = event.target.value;
                const inspection = inspectEmphasisTermsText(value);
                setDrafts((current) => ({ ...current, [field.key]: value }));
                if (inspection.valid) {
                  onChange({
                    ...rules,
                    [field.key]: inspection.terms,
                  });
                }
              }}
              placeholder={
                field.key === "bold"
                  ? "例如：p<0.05\n显著差异"
                  : field.key === "italic"
                    ? "例如：Arabidopsis thaliana"
                    : "例如：关键结论"
              }
              rows={compact ? 4 : 6}
              spellCheck={false}
              value={drafts[field.key]}
            />
            {!inspectEmphasisTermsText(drafts[field.key]).valid ? (
              <span className="emphasis-rule-error" role="alert">
                {inspectEmphasisTermsText(drafts[field.key]).tooLongCount
                  ? `${inspectEmphasisTermsText(drafts[field.key]).tooLongCount} 条超过 200 字，请缩短后再保存。`
                  : "每组最多 200 条，请删除超出部分后再保存。"}
              </span>
            ) : null}
          </label>
        ))}
      </div>
      <div className="emphasis-rules-note">
        <span>
          共 {total} 条。每行一个词或短语（每组最多 200 条、每条最多 200 字），区分大小写，
          按原文字面完全匹配；不支持正则，不会改写原文。
        </span>
        <span>
          作用于摘要、关键词、H1–H3、正文、声明、表格、图注和参考文献；作者、邮箱、机构等署名信息不自动处理。
        </span>
        {conflicts.length ? (
          <strong role="status">
            {conflicts.slice(0, 4).map((term) => `“${term}”`).join("、")}
            {conflicts.length > 4 ? ` 等 ${conflicts.length} 项` : ""}
            同时出现在多组中，按“加粗并斜体 ＞ 加粗 ＞ 斜体”处理。
          </strong>
        ) : null}
      </div>
    </div>
  );
}
